import React, { useState } from 'react';

import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import { useGetTaxEstimation } from '~/hooks/apis/connection-google/useTaxEstimation';

import ApiError from '~/types/ApiError';

import { formatShare, formatTimestamp } from './utils';

export default function TaxEstimationPanel() {
  const [propertyId, setPropertyId] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [lookup, setLookup] = useState<{ propertyId?: string; currency: string }>({ currency: 'USD' });
  const query = useGetTaxEstimation(lookup.propertyId, lookup.currency);
  if (query.isPending) return <CircularProgress aria-label="Loading tax estimates" />;
  if (query.isError) {
    const unauthorised = query.error instanceof ApiError && query.error.status === 403;
    return (
      <Stack spacing={2}>
        <Alert severity="error">
          {unauthorised ? 'You are not authorised to view tax estimates.' : 'Unable to load tax estimates.'}
        </Alert>
        <Button onClick={() => query.refetch()}>Retry</Button>
      </Stack>
    );
  }
  const { cache, properties } = query.data;
  const profile = properties[0]?.profiles[0];
  return (
    <Stack spacing={3}>
      {cache.status === 'missing' && <Alert severity="warning">No tax estimate snapshot is cached.</Alert>}
      <Box>
        <Typography variant="h6">Cached snapshot</Typography>
        <Typography>Snapshot date: {cache.snapshotDate ?? 'Not available'}</Typography>
        <Typography>Last refreshed: {formatTimestamp(cache.refreshedAt)}</Typography>
        <Typography>Expires: {formatTimestamp(cache.expiresAt)}</Typography>
        <Typography variant="body2">Refresh and expiry times use your browser timezone.</Typography>
      </Box>
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          if (propertyId.trim() && /^[A-Za-z]{3}$/.test(currency)) {
            setLookup({ propertyId: propertyId.trim(), currency: currency.toUpperCase() });
          }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Property ID"
            value={propertyId}
            onChange={(event) => setPropertyId(event.target.value)}
            size="small"
            required
          />
          <TextField
            label="Currency"
            value={currency}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
            size="small"
            required
            inputProps={{ minLength: 3, maxLength: 3 }}
          />
          <Button type="submit" variant="contained" disabled={!propertyId.trim() || !/^[A-Z]{3}$/.test(currency)}>
            Look up
          </Button>
          <Button
            onClick={() => {
              setPropertyId('');
              setCurrency('USD');
              setLookup({ currency: 'USD' });
            }}
          >
            Clear
          </Button>
        </Stack>
        <Typography variant="body2">Market: US</Typography>
      </Box>
      {lookup.propertyId && (
        <Box>
          <Typography>Property ID: {lookup.propertyId}</Typography>
          <Typography>
            {profile ? `${profile.currency} government tax share: ${formatShare(profile.share)}` : 'No estimate found.'}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
